import React, { useState, useEffect } from 'react';

import './ProfileSettings.scss';

import icons from '../../shared/assets/icons';
import { getString } from '../../helpers/styles';
import { sliceLength } from '../../helpers/form';
import setUserPhoto from '../../helpers/setUserPhoto';
import BackButton from '../BackButton/BackButton.jsx';
import CalendarPopup from '../CalendarPopup/CalendarPopup.jsx';
import { format as formatDate, buildDateString } from '../../helpers/date';

const { Edit, CheckBoxCircle, ArrowUp, ArrowDown, Calendar } = icons;

const pregnancyStatuses = {
  PREGNANT: 'Nu zwanger',
  DELIVERED: 'Bevallen',
  ENDED: 'Zwangerschap vroegtijdig beëindigd',
  PREPARING: 'Voorbereiding op zwangerschap',
  OTHER: 'Anders'
};

export default function ProfileSettings(props) {
  const {
    user,
    changeUserData,
    showEmailPreferenciesPopup,
    setUserPhoto: setUserPhotoMutation
  } = props;

  const [popup, showPopup] = useState(false);
  const [subItemDate, setSubItemDate] = useState(null);
  const [iOSdatepicker, useIOSdatepicker] = useState(false);
  const [validationError, setValidationError] = useState({});
  const [userPhoto, updateUserPhoto] = useState(user.profilePicture);
  const [providerListPopup, showProviderListPopup] = useState(false);

  const [settings, updateSettings] = useState([
    {
      title: 'Jouw zwangerschap',
      items: [
        {
          name: 'status',
          label: 'Status',
          type: 'valueSet',
          valueSet: pregnancyStatuses,
          value: pregnancyStatuses[user.status],
          subItem: {
            'Nu zwanger': {
              name: 'edd',
              type: 'date',
              isLimitedPeriod: true,
              value: format(user.edd),
              placeholder: 'MM-DD-YYYY',
              label: 'Uitgerekende datum'
            },
            Bevallen: {
              type: 'date',
              label: 'Bevallen op',
              name: 'pregnancyEndDate',
              placeholder: 'MM-DD-YYYY',
              value: format(user.pregnancyEndDate)
            },
            'Zwangerschap vroegtijdig beëindigd': {
              type: 'date',
              name: 'pregnancyEndDate',
              label: 'Beëindigd per',
              placeholder: 'MM-DD-YYYY',
              value: format(user.pregnancyEndDate)
            }
          }
        }
      ]
    },
    {
      title: 'Jouw profiel',
      items: [
        {
          label: 'Wachtwoord',
          type: 'password',
          value: '********'
        },
        {
          name: 'postcode',
          label: 'Mijn postcode (eerste vier cijfers)',
          value: user.postcode,
          type: 'text'
        },
        {
          type: 'custom',
          name: 'provider',
          label: 'Mijn zorgverlener',
          value: user.provider && user.provider.accountName,
          displayedValue: user.provider && user.provider.displayName
        },
        {
          name: 'weekMail',
          label: 'Weekmail ontvangen',
          type: 'valueSet',
          value: user.weekMail ? 'Ja' : 'Nee',
          valueSet: { Y: 'Ja', N: 'Nee' }
        },
        {
          name: 'emailPreferencies',
          label: 'Overige e-mail voorkeuren',
          noValue: true
        },
        {
          name: 'premium',
          label: 'Aangemeld voor familie voordeel programma',
          type: 'valueSet',
          value: user.premium ? 'Ja' : 'Nee',
          valueSet: { Y: 'Ja', N: 'Nee' }
        }
      ]
    },
    {
      title: 'Kanaal gegevens',
      items: [
        {
          name: 'bio',
          label: 'Jouw bio',
          type: 'textArea',
          value: user.bio
        }
      ]
    },
    {
      title: 'Social media',
      items: [
        {
          name: 'twitter',
          label: 'Twitter',
          value: user.twitter,
          type: 'text'
        },
        {
          name: 'instagram',
          label: 'Instagram',
          value: user.instagram,
          type: 'text'
        },
        {
          name: 'facebook',
          label: 'Facebook',
          value: user.facebook,
          type: 'text'
        },
        {
          name: 'website',
          label: 'Website',
          value: user.website,
          type: 'text'
        }
      ]
    }
  ]);

  useEffect(() => {
    document.body.addEventListener('click', closeProviderListPopup);

    const iOS = /iPad|iPhone|iPod/.test(navigator.platform);

    if (iOS) useIOSdatepicker(true);

    document.body.addEventListener('click', closePopup);

    return () => document.body.removeEventListener('click', closePopup);
  }, []);

  return (
    <section id="profileSettings">
      <BackButton />

      <div id="profileSettings_content">
        <div id="profileSettings_image">
          <img src={userPhoto} alt="user-image" />

          <p onClick={() => openFileDialog()}>Profielfoto wijzigen</p>

          <input
            type="file"
            accept="image/*"
            onChange={(e) => setPhoto(e)}
            id="profileSettings_photo_button"
          />
        </div>

        <div id="profileSettings_list">
          {settings.map((section, sectionId) => (
            <div className="profileSettings_section" key={sectionId}>
              <h3>{section.title}</h3>

              <ul>
                {section.items.map((item, idx) => (
                  <li key={idx}>
                    <div>
                      {itemBlock({ item, sectionId, editBlock: editItemBlock })}

                      {item.subItem && item.subItem[item.value] && (
                        <div className="profileSettings_subItem">
                          {itemBlock({
                            item,
                            subItem: item.subItem[item.value],
                            sectionId,
                            editBlock: editSubItemBlock
                          })}

                          {item.subItem[item.value].name === 'edd' && (
                            <a
                              className="profileSettings_edd_link"
                              href="/zwanger/uitgerekende-datum"
                            >
                              Uitgerekende datum berekenen?
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    {item.label === 'Wachtwoord' ? (
                      <a href={props.passwordResetUrl}>
                        <span>
                          <Edit />
                        </span>
                      </a>
                    ) : item.name === 'emailPreferencies' ? (
                      <span onClick={() => showEmailPreferenciesPopup(true)}>
                        <Edit />
                      </span>
                    ) : item.editState ? (
                      <span
                        onClick={(e) => saveChanges(e, sectionId, item.label)}
                      >
                        <CheckBoxCircle />
                      </span>
                    ) : (
                      <span onClick={() => setEditState(sectionId, item.label)}>
                        <Edit />
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  function getTargetValue(item) {
    if (item.value === 'Niet opgegeven') return null;

    if (item.type === 'date')
      return iOSdatepicker ? item.value : format(item.value);

    if (item.valueSet)
      return Object.keys(item.valueSet).find(
        (key) => item.valueSet[key] === item.value
      );

    if (item.value.match(/twitter.com\/|instagram.com\/|facebook.com\//))
      return item.value.split('/').pop();

    return item.value;
  }

  function closePopup(e) {
    if (e.target.closest('#calendarPopup')) return;

    showPopup(false);
  }

  function itemBlock({ item, subItem, sectionId, editBlock }) {
    const entry = subItem || item;

    const isProviderField = entry.name === 'provider';

    const value =
      (entry.displayedValue && entry.displayedValue.trim()) ||
      (entry.value && entry.value.trim());

    return (
      <React.Fragment>
        <p>{entry.label}</p>

        {validationError[entry.name] && (
          <p className="profileSettings_validation_error">
            {validationError[entry.name]}
          </p>
        )}

        {!entry.noValue &&
          (item.editState ? (
            editBlock(entry, sectionId, item.value)
          ) : (
            <span
              className={getString(
                'profileSettings_marked',
                (isProviderField || !entry.value || !entry.value.trim()) &&
                  !entry.valueSet
              )}
            >
              {value || 'Niet opgegeven'}
            </span>
          ))}
      </React.Fragment>
    );
  }

  function editItemBlock(item, sectionId) {
    switch (item.type) {
      case 'text':
        return (
          <input
            type="text"
            defaultValue={item.value}
            className="profileSettings_input"
          />
        );

      case 'textArea':
        return (
          <textarea
            autoFocus
            rows="10"
            defaultValue={item.value}
            className="profileSettings_input profileSettings_input_area"
          />
        );

      case 'valueSet':
        return (
          <div className="profileSettings_value_set">
            <div
              onClick={(e) => toggleDropdownMenu(e, sectionId, item.label)}
              className={getString(
                'profileSettings_folded_out',
                item.foldedOut
              )}
            >
              <input
                readOnly
                value={item.value}
                className="profileSettings_input"
              />

              {item.foldedOut ? <ArrowUp /> : <ArrowDown />}
            </div>

            {item.foldedOut && (
              <ul onClick={(e) => setSelectedValue(e, sectionId, item.label)}>
                {Object.values(item.valueSet).map(
                  (value) =>
                    item.value !== value && <li key={value}>{value}</li>
                )}
              </ul>
            )}
          </div>
        );

      case 'custom':
        switch (item.name) {
          case 'provider':
            return editProviderBlock(item, sectionId);
        }
    }
  }

  function editSubItemBlock(item) {
    const [year, month] = getCalendarPopupDate();

    const defaultValue =
      subItemDate &&
      `${subItemDate.day < 10 ? `0${subItemDate.day}` : subItemDate.day}-${
        subItemDate.month < 10 ? `0${subItemDate.month}` : subItemDate.month
      }-${subItemDate.year}`;

    const date = new Date();

    const minDate = buildDateString(date);

    // 40 weeks from now
    date.setDate(date.getDate() + 40 * 7);

    const maxDate = buildDateString(date);

    return iOSdatepicker ? (
      <input
        required
        type="date"
        min={minDate}
        max={maxDate}
        placeholder="Datum"
        defaultValue={defaultValue}
        id="profileSettings_selected_date"
        className="profileSettings_input
          profileSettings_date_input profileSettings_edd_input"
      />
    ) : (
      <React.Fragment>
        <input
          data-validated-value=""
          placeholder={item.placeholder}
          id="profileSettings_selected_date"
          onChange={(e) => formatDate(e.target)}
          onClick={(e) => (e.target.placeholder = 'DD-MM-JJJJ')}
          pattern="(0[1-9]|1[0-9]|2[0-9]|3[01])-(0[1-9]|1[012])-[0-9]{4}"
          className="profileSettings_input
            profileSettings_date_input profileSettings_edd_input"
          defaultValue={defaultValue}
        />

        <span id="profileSettings_calendar" onClick={() => showPopup(!popup)}>
          <Calendar />
        </span>

        {popup && (
          <CalendarPopup
            year={year}
            month={month}
            withTriangle={true}
            togglePopup={showPopup}
            field="profileSettings_selected_date"
            changeSelectedDate={setSubItemDate}
            {...(item.isLimitedPeriod && { isLimitedByPregnancyPeriod: true })}
          />
        )}
      </React.Fragment>
    );

    function getCalendarPopupDate() {
      const date = new Date();

      if (item.name === 'edd') {
        // 40 weeks from now
        date.setDate(date.getDate() + 40 * 7);

        return [date.getFullYear(), date.getMonth() + 1];
      }

      return [
        (subItemDate || {}).year || date.getFullYear(),
        (subItemDate || {}).month || date.getMonth() + 1
      ];
    }
  }

  function editProviderBlock(item, sectionId) {
    const orgs = props.organizations.searchOrganizationByPostalcode;

    return (
      <div className="profileSettings_user_provider_setting">
        <input
          readOnly
          data-value={item.value}
          value={item.displayedValue || 'Niet opgegeven'}
          className="profileSettings_input profileSettings_user_provider"
        />

        <form
          onSubmit={(e) => searchOrganizations(e)}
          className="profileSettings_user_provider_search_form"
        >
          <input
            required
            name="code"
            type="number"
            placeholder="Postcode"
            onChange={(e) => sliceLength(e.target, 4)}
          />

          <button
            data-search-provider-button
            className="button button-primary"
            tabIndex="0"
            type="submit"
          >
            {props.organizationsLoading ? 'Loading...' : 'Zoeken'}
          </button>
        </form>

        <button
          onClick={(e) => setProvider(e)}
          className="button button-secondary"
        >
          Verwijder zorgverlener
        </button>

        {providerListPopup && orgs ? (
          orgs.length ? (
            <ul
              className="profileSettings_provider_list"
              onClick={(e) => setProvider(e, sectionId, orgs)}
            >
              {orgs.map((item) => (
                <li data-id={item.id} key={item.id}>
                  {item.displayName}
                </li>
              ))}
            </ul>
          ) : (
            <div className="profileSettings_no_result_message">
              Er zijn geen organisaties gevonden in dit postcodegebied, probeer
              het opnieuw.
            </div>
          )
        ) : null}
      </div>
    );

    function setProvider(e, id, orgs = []) {
      const org = orgs.find((org) => org.id === e.target.dataset.id) || {};

      const modifiedSettings = [...settings];

      const item = modifiedSettings[sectionId].items.find(
        (item) => item.name === 'provider'
      );

      item.value = org?.accountName;
      item.displayedValue = org?.displayName;

      updateSettings(modifiedSettings);

      showProviderListPopup(false);
    }
  }

  function toggleDropdownMenu(e, id, label) {
    const modifiedSettings = [...settings];

    const item = modifiedSettings[id].items.find(
      (item) => item.label === label
    );

    item.foldedOut = !item.foldedOut;

    updateSettings(modifiedSettings);
  }

  function setSelectedValue(e, id, label) {
    const modifiedSettings = [...settings];

    const item = modifiedSettings[id].items.find(
      (item) => item.label === label
    );

    item.foldedOut = false;
    item.value = e.target.textContent;

    updateSubFieldValue(item);

    updateSettings(modifiedSettings);
  }

  function setEditState(id, label) {
    if (label === 'Wachtwoord') return (location.href = props.passwordResetUrl);

    setValidationError({});

    const modifiedSettings = [...settings];

    const item = modifiedSettings[id].items.find(
      (item) => item.label === label
    );

    item.editState = true;

    updateSubFieldValue(item);

    updateSettings(modifiedSettings);
  }

  function saveChanges(e, id, label) {
    setValidationError({});

    const inputs = e.target
      .closest('li')
      .querySelectorAll('.profileSettings_input');

    const modifiedSettings = [...settings];

    const item = modifiedSettings[id].items.find(
      (item) => item.label === label
    );
    const subItem = item.subItem && item.subItem[item.value];

    const itemValue = inputs[0].value;

    if (!isValid(item, itemValue))
      return setValidationError({ [item.name]: item.validation.text });

    item.editState = false;
    item.value = inputs[0].dataset.value || inputs[0].value;

    if (subItem) subItem.value = inputs[1].value;

    updateSettings(modifiedSettings);

    changeUserData({
      variables: {
        [item.name]: getTargetValue(item),
        ...(subItem && { [subItem.name]: getTargetValue(subItem) })
      }
    });
  }

  function setPhoto(e) {
    if (e.target.files && e.target.files[0])
      setUserPhoto({
        updateUserPhoto,
        setUserPhotoMutation,
        file: e.target.files[0]
      });
  }

  function openFileDialog() {
    document.getElementById('profileSettings_photo_button').click();
  }

  function updateSubFieldValue(item) {
    const subItem = item.subItem && item.subItem[item.value];

    if (!subItem) return;

    if (!subItem.value) return setSubItemDate(null);

    const date = new Date(subItem.value.split('-').reverse().join('/'));

    setSubItemDate({
      day: date.getDate(),
      year: date.getFullYear(),
      month: date.getMonth() + 1
    });
  }

  function searchOrganizations(e) {
    e.preventDefault();

    const value = e.target.code.value.trim();

    props.getOrganizations({
      variables: { postalcode: value, distance: 1000 }
    });

    showProviderListPopup(true);
  }

  function closeProviderListPopup(e) {
    if (e.target.closest('.profileSettings_provider_list')) return;

    showProviderListPopup(false);
  }
}

function isValid(item, value) {
  return !item.validation || !value || value.match(item.validation.regexp);
}

function format(date) {
  if (!date) return '';

  return date.split('-').reverse().join('-');
}
